// Locker providers (IdFabricante), from the catalog ConfObjetosDetallesDescripciones IdObjeto=67.
// api-boxit-admin-usa sends each provider with its `capabilities`; the lists below are only
// the fallback for a catalog without them (older API, or a list cached before the change).

// Caché del catálogo en sessionStorage. Sube de versión cuando cambie la forma del payload;
// signOut la borra para que el próximo login (u otro país) lo vuelva a pedir.
export const PROVIDERS_STORAGE_KEY = 'providerPlatformsV4'; // V4: capabilities con lampHours, sharedIp y networkPort

export const ZHILAI_PROVIDER_ID = 4;
export const MERCURY_PROVIDER_ID = 5;
// Multi-Vendor: el locker mezcla proveedores y cada módulo elige el suyo
export const MULTI_VENDOR_PROVIDER_ID = 6;

// IdTipoComunicacion del módulo: 1 serial (COM), 2 red (IP)
const COMMUNICATION_TYPE_NETWORK = 2;
// TipoLocker del locker
export const LOCKER_TYPE = { INDOOR: 1, OUTDOOR: 2 };
// en qué lockers van las horas de lámpara
export const LAMP_HOURS = { OUTDOOR: 'outdoor', NONE: 'none' };

const NETWORK_PROVIDER_IDS = [ZHILAI_PROVIDER_ID, MERCURY_PROVIDER_ID];
const MONITORING_PROVIDER_IDS = [ZHILAI_PROVIDER_ID];
// Mercury: sin horas de lámpara, el tipo Indoor/Outdoor es solo informativo
const NO_LAMP_HOURS_PROVIDER_IDS = [MERCURY_PROVIDER_ID];
// Mercury: un solo controlador, todos los módulos con la misma IP
const SHARED_IP_PROVIDER_IDS = [MERCURY_PROVIDER_ID];

// Mercury: además de la IP, cada módulo puede llevar su puerto TCP (se guarda en Com; vacío, NULL)
const NETWORK_PORT_PROVIDER_IDS = [MERCURY_PROVIDER_ID];

export const REGEX_IP_ADDRESS = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
export const isValidNetworkPort = (port) => /^\d{1,5}$/.test(String(port ?? '')) && Number(port) >= 1 && Number(port) <= 65535;

const fallbackCapabilities = (id) => ({
  network: NETWORK_PROVIDER_IDS.includes(id),
  monitoring: MONITORING_PROVIDER_IDS.includes(id),
  lampHours: NO_LAMP_HOURS_PROVIDER_IDS.includes(id) ? LAMP_HOURS.NONE : LAMP_HOURS.OUTDOOR,
  sharedIp: SHARED_IP_PROVIDER_IDS.includes(id),
  networkPort: NETWORK_PORT_PROVIDER_IDS.includes(id),
  multiVendor: id === MULTI_VENDOR_PROVIDER_ID,
});

/**
 * What a provider's lockers carry:
 * - network: modules use an IP address instead of a COM port
 * - monitoring: outdoor lockers get a monitoring module
 * - lampHours: 'outdoor' shows them only on Outdoor lockers, required; 'none' never shows them
 * - sharedIp: every module keeps the same IP instead of one per module
 * - networkPort: each network module also carries its TCP port (stored in Com)
 * - multiVendor: each module names its own provider, so network and monitoring are per module
 * Lo que el catálogo no trae (API anterior) sale del respaldo local.
 */
export const getProviderCapabilities = (providerId, providers = []) => {
  const id = Number(providerId);
  if (!id) return null;
  const fromCatalog = providers?.find(provider => Number(provider.id) === id)?.capabilities;
  return { ...fallbackCapabilities(id), ...fromCatalog };
};

/**
 * Horas de lámpara según proveedor y tipo de locker: Zhilai y los demás las piden en Outdoor;
 * Mercury no las lleva nunca.
 */
export const getLampHoursSetup = (providerId, typeLocker, providers = []) => {
  if (getProviderCapabilities(providerId, providers)?.lampHours === LAMP_HOURS.NONE) {
    return { hidden: true, required: false };
  }
  return { hidden: Number(typeLocker) !== LOCKER_TYPE.OUTDOOR, required: true };
};

// proveedor de un módulo: el suyo en Multi-Vendor, el del locker en los demás
const getModuleCapabilities = (module, lockerProviderId, providers = []) => {
  const locker = getProviderCapabilities(lockerProviderId, providers);
  return locker?.multiVendor ? getProviderCapabilities(module?.providerId, providers) : locker;
};

/**
 * Si el módulo va por red (IP) o serial (COM). En un locker Multi-Vendor es del módulo:
 * el guardado por su IdTipoComunicacion, el borrador por el proveedor que se le eligió.
 */
export const isModuleNetwork = (module, lockerProviderId, providers = []) => {
  const locker = getProviderCapabilities(lockerProviderId, providers);
  if (locker?.multiVendor && module?.communicationTypeId) return Number(module.communicationTypeId) === COMMUNICATION_TYPE_NETWORK;
  return !!getModuleCapabilities(module, lockerProviderId, providers)?.network;
};

// Si el módulo comparte la IP con los demás módulos de su proveedor (Mercury)
export const isModuleSharedIp = (module, lockerProviderId, providers = []) =>
  !!getModuleCapabilities(module, lockerProviderId, providers)?.sharedIp;

// Si el módulo lleva puerto TCP además de la IP (Mercury, también dentro de un Multi-Vendor)
export const isModuleNetworkPort = (module, lockerProviderId, providers = []) =>
  isModuleNetwork(module, lockerProviderId, providers)
  && !!getModuleCapabilities(module, lockerProviderId, providers)?.networkPort;
